import { useTranslation } from 'react-i18next';
import {
  CarrierContactDetailOutDTO,
  CarrierQuoteStateEnum,
  CustomerQuoteStateEnum,
  ExpeditionDetailV4OutDTO,
  ExpeditionStatusEnum,
  LocationPreviewV4OutDTO,
  QuotesPreviewOutDTO,
} from '@api/ringil-api.v2.tsx';
import { useContext } from 'react';
import { ApiContext } from '@api/api.tsx';
import { parsePhoneFromNote } from '@components/RingilComponents/InputPhoneNumber/InputPhoneNumber.tsx';
import sanitizeHtml from 'sanitize-html';

const BULLET = '\u2022';
const SUB_BULLET = '\u25E6';
const SUB_SUB_BULLET = '\u25AA';

const formatOptional = (value: string | null | undefined, fallback: string): string =>
  value ? value : fallback;

const formatDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

// The carrier note is now WYSIWYG HTML; the mailto body is plaintext, so down-convert block/br to newlines then strip tags.
const htmlNoteToPlainText = (html: string): string => {
  if (!html) return '';
  const withBreaks = html
    .replace(/<\/(p|div|li|blockquote|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n');
  return sanitizeHtml(withBreaks, { allowedTags: [], allowedAttributes: {} })
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
};

export const useExpeditionMailto = () => {
  const { t } = useTranslation();
  const { getQuotesDbPaged } = useContext(ApiContext);

  const fetchQuotesForDistribution = (
    expeditionId: string,
    distributionId: string,
  ): Promise<QuotesPreviewOutDTO[]> => {
    return new Promise((resolve) => {
      getQuotesDbPaged(
        expeditionId,
        distributionId,
        { },
        { pageNumber: 0, pageSize: 1000 },
        (response) => {
          resolve(response?.data ?? []);
        },
      );
    });
  };

  const buildMailto = async (data: ExpeditionDetailV4OutDTO, showPrice: boolean): Promise<string> => {

    const na = t('expedition.notify.mailto.notAvailable', 'N/A');

    const requestNumber = data?.internalInfo?.pairingSymbol ?? na;
    const createdAt = data?.createdAt
      ? formatDate(new Date(data.createdAt))
      : na;
    const rawInternalNote = data?.internalInfo?.internalNote ?? '';
    const internalNoteFiltered = rawInternalNote
      .split('\n')
      .filter(line => !line.trim().startsWith('REQUESTEDBY_ID'))
      .map(line => line.replace(/CURRENCY\s*-\s*\S+/g, '').trim())
      .join('\n')
      .trim();
    const internalNote = internalNoteFiltered || na;
    const responsiblePersonName = formatOptional(data?.internalInfo?.responsiblePerson?.userFullName, na);
    const responsiblePersonPhone = formatOptional(data?.internalInfo?.responsiblePerson?.phoneNumber, na);

    const acceptedLadenExpedition = data.ladenFactorExpeditions.filter(expedition => expedition.expeditionStatus !== ExpeditionStatusEnum.CANCELED);
    const pricedLadenExpedition = acceptedLadenExpedition.filter(expedition => expedition.priceDTO != null);

    const allQuotesArrays = await Promise.all(
      (data?.distributions ?? []).map(distr =>
        fetchQuotesForDistribution(data.id, distr.distributionId),
      ),
    );

    const acceptedQuotes = allQuotesArrays
      .flat()
      .filter(q => q.carrierQuoteState === CarrierQuoteStateEnum.ACCEPTED_BY_CUSTOMER
        || q.carrierQuoteState === CarrierQuoteStateEnum.ACCEPTED_NEED_RECONFIRM
        || q.customerQuoteState === CustomerQuoteStateEnum.ASSIGNED_MANUALLY_BY_CUSTOMER);

    const currencies: string[] = (acceptedLadenExpedition && acceptedLadenExpedition.length > 0)
      ? pricedLadenExpedition
        ?.map(exp => exp.priceDTO.currency).filter((v, i, arr) => arr.indexOf(v) === i) // unique
      : acceptedQuotes
        ?.map(q => q.priceDTO?.currency)
        ?.filter(currency => currency != null)
        ?.filter((v, i, arr) => arr.indexOf(v) === i) // unique
      ?? [];

    const currencyText = currencies.length > 0
      ? currencies.join(', ')
      : na;

    const numberOfCarsValue = (data?.handlingUnitCount != null)
      ? String(data.handlingUnitCount)
      : undefined;
    const numberOfCars = formatOptional(numberOfCarsValue, na);
    const rawCarrierNote = htmlNoteToPlainText(data?.carrierNote ?? '');
    const carrierNoteFiltered = rawCarrierNote
      .split('\n')
      .filter(line => line.trim().startsWith('TRANSPORT_TYPE'))
      .join('\n')
      .trim();
    const carrierNote = carrierNoteFiltered || na;
    const ladenFactor = formatOptional(String(data?.ladenFactor), na);
    const costCenter = formatOptional(data?.internalInfo?.internalInfoCostCenter?.externalId, na);

    const seenCarrierIds = new Set<string>();
    const dedupeByCarrierId = <T extends { carrierId?: string }>(items: T[]): T[] =>
      items.filter(item => {
        const id = item.carrierId ?? '';
        if (seenCarrierIds.has(id)) return false;
        seenCarrierIds.add(id);
        return true;
      });

    const quoteByCarrierId = new Map(
      (acceptedQuotes ?? []).map(q => [q.carrierId, q]),
    );

    type CarrierEntry = {
      carrierName?: string;
      carrierId?: string;
      carrierContactDetails?: CarrierContactDetailOutDTO;
      note?: string;
    };

    const formatCarrierEntry = (entry: CarrierEntry): string => {
      const carrierName = entry.carrierName ?? na;
      const carrierId = entry.carrierContactDetails?.externalIdsInfo?.length > 0
        ? entry.carrierContactDetails.externalIdsInfo.map(externalId => `${externalId.externalIdType}: ${externalId.externalIds}`).join(', ')
        : entry.carrierId ?? na;
      const contactName = entry.carrierContactDetails?.fullName ?? na;
      const contactEmail = entry.carrierContactDetails?.email ?? na;
      const parsedFromNote = parsePhoneFromNote(entry.note);
      const parsedPhone = parsedFromNote ? `${parsedFromNote.prefix} ${parsedFromNote.number}` : undefined;
      const existingPhone = entry.carrierContactDetails?.phone;
      const contactPhone = parsedPhone && existingPhone
        ? `${parsedPhone} | ${existingPhone}`
        : (parsedPhone ?? existingPhone ?? na);

      return [
        `   ${SUB_BULLET} ${carrierName} - ${carrierId}`,
        `      ${SUB_SUB_BULLET} ${t('expedition.notify.mailto.carrierContactName', 'Carrier contact name')}: ${contactName}`,
        `      ${SUB_SUB_BULLET} ${t('expedition.notify.mailto.carrierContactEmail', 'Carrier contact email')}: ${contactEmail}`,
        `      ${SUB_SUB_BULLET} ${t('expedition.notify.mailto.carrierContactPhone', 'Carrier contact phone')}: ${contactPhone}`,
      ].join('\r\n');
    };

    const carrierEntries: CarrierEntry[] = (acceptedLadenExpedition && acceptedLadenExpedition.length > 0)
      ? dedupeByCarrierId(pricedLadenExpedition).map(exp => ({
        carrierName: exp.carrierName,
        carrierId: exp.carrierId,
        carrierContactDetails: exp.carrierContactDetails,
        note: quoteByCarrierId.get(exp.carrierId)?.quoteDemandNote,
      }))
      : dedupeByCarrierId(acceptedQuotes ?? []).map(q => ({
        carrierName: q.carrierName,
        carrierId: q.carrierId,
        carrierContactDetails: q.carrierContactDetails,
        note: q.quoteDemandNote,
      }));

    const carrierLines = carrierEntries.map(formatCarrierEntry).join('\r\n');

    const formatStop = (location: LocationPreviewV4OutDTO): string => {
      const lines = [
        `   ${SUB_BULLET} ${location.name}`,
        `      ${SUB_SUB_BULLET} ${formatOptional(location?.country, na)}, ${formatOptional(location?.postalCode, na)}, ${formatOptional(location?.city, na)}, ${formatOptional(location?.streetNr, na)}`,
        `      ${SUB_SUB_BULLET} ${t('expedition.notify.mailto.loadedOrders', 'Loaded orders')}: ${location?.loadedOrders?.map(order => order.orderName).join(', ') || na}`,
        `      ${SUB_SUB_BULLET} ${t('expedition.notify.mailto.unLoadedOrders', 'Unloaded orders')}: ${location?.unloadedOrders?.map(order => order.orderName).join(', ') || na}`,
      ];
      return lines.join('\r\n');
    };

    const stopsText = (data?.locations ?? [])
      .map(location => formatStop(location))
      .join('\r\n');


    const expeditionPrice = (acceptedLadenExpedition && acceptedLadenExpedition.length > 0)
      ? pricedLadenExpedition.map(exp => {
        const carrierName = exp.carrierName ?? na;
        const price = exp.priceDTO.price ?? na;
        const currency = exp.priceDTO.currency ?? na;

        return [
          `   ${SUB_BULLET} ${carrierName} - ${price} ${currency}`,
        ];
      }).join('\r\n')
      : (acceptedQuotes ?? []).map(q => {
        const carrierName = q.carrierName ?? na;
        const price = q.priceDTO?.price ?? na;
        const currency = q.priceDTO?.currency ?? na;

        return [
          `   ${SUB_BULLET} ${carrierName} - ${price} ${currency}`,
        ];
      }).join('\r\n');


    const body = [
      `${t('expedition.notify.mailto.greet', 'Dear colleaguesr')}`,
      ` `,
      `${t('expedition.notify.mailto.infoAbout', 'We are sending information about shipment: {{applicationId}}', { applicationId: data.applicationId })}`,
      ` `,
      `${BULLET} ${t('expedition.notify.mailto.requestNumber', 'Request number')}: ${requestNumber}`,
      `${BULLET} ${t('expedition.notify.mailto.createdAt', 'Created at')}: ${createdAt}`,
      `${BULLET} ${t('expedition.notify.mailto.internalNote', 'Internal note')}: ${internalNote}`,
      `${BULLET} ${t('expedition.notify.mailto.responsiblePersonContact', 'Responsible person contact')}:`,
      `   ${SUB_BULLET} ${t('expedition.notify.mailto.name', 'Name')}: ${responsiblePersonName}`,
      `   ${SUB_BULLET} ${t('expedition.notify.mailto.phone', 'Phone')}: ${responsiblePersonPhone}`,
      `${BULLET} ${t('expedition.notify.mailto.currency', 'Currency')}: ${currencyText}`,
      `${BULLET} ${t('expedition.notify.mailto.numberOfCars', 'Number of cars')}: ${numberOfCars}`,
      `${BULLET} ${t('expedition.notify.mailto.carrierNote', 'Carrier note')}: ${carrierNote}`,
      `${BULLET} ${t('expedition.notify.mailto.ladenFactor', 'Ladenfactor')}: ${ladenFactor}`,
      `${BULLET} ${t('expedition.notify.mailto.costCenter', 'Cost center')}: ${costCenter}`,
      `${BULLET} ${t('expedition.notify.mailto.carriers', 'Carriers')}:`,
      carrierLines.length > 0 ? carrierLines : `   ${SUB_BULLET} ${na}`,
      `${BULLET} ${t('expedition.notify.mailto.locations', 'Location')}:`,
      stopsText,
      ...(showPrice ? [
        `${BULLET} ${t('expedition.notify.mailto.winningPrice', 'Winning price')}:`,
        expeditionPrice,
      ] : []),
    ].join('\r\n');

    const subject = t('expedition.notify.mailto.subject', 'Subject', { requestNumber });
    const encodedBody = encodeURIComponent(body);
    const encodedSubject = encodeURIComponent(subject);

    // Recipients (To) are resolved by the backend from the company code-list notification
    // emails setup (NS / cost center + plant) and delivered already flattened. Same recipients
    // for the with-price and without-price variants; only the body differs.
    const recipients = (data?.internalInfo?.codeListNotificationEmails ?? []).join(';');

    return `mailto:${recipients}?subject=${encodedSubject}&body=${encodedBody}`;
  };

  return { buildMailto };
};